/**
 * Catalog descriptor for a scopable entity.
 *
 * Parallel to {@link IPermission} (the flat string-permission catalog), but for
 * data entities whose access is governed by an (action, scope) pair. Each module
 * exports an `IScopedEntity[]` under `shared/permissions/`; they are aggregated
 * into the `ScopedPermissionManager` at server startup and drive the future
 * permission matrix UI (rows = entity×action, columns = scope).
 */
import type { PermissionAction, PermissionActionKey, PermissionScope } from './scope';
import { SCOPE_ORDER } from './scope';

/**
 * A custom (non-CRUD) action a module declares for an entity, with an optional
 * i18n label and per-action scope restriction. Use this in `IScopedEntity.actions`
 * alongside bare CRUD strings.
 */
export interface IScopedActionDescriptor {
  /** Catalog action key — persisted in `rolePermissionTable.action` (e.g. 'sendEmail'). */
  action: PermissionActionKey;
  /** i18n key for the row label; defaults to `permission.action.{action}`. */
  label?: string;
  /** Which scope columns are valid for this action; falls back to the entity's scopes. */
  scopes?: readonly PermissionScope[];
}

export interface IScopedEntity {
  /** Catalog key — must match `rolePermissionTable.entity` (e.g. 'person'). */
  entity: string;
  /** i18n key for the entity's display label. */
  label: string;
  /** i18n key for the group/module this entity belongs to. */
  group: string;
  /**
   * Which actions apply to this entity (matrix rows = entity × these actions).
   * Each entry is either a bare CRUD verb or a custom action descriptor.
   */
  actions: readonly (PermissionAction | IScopedActionDescriptor)[];
  /** Which scope columns are valid; defaults to all five when omitted. */
  scopes?: readonly PermissionScope[];
}

/**
 * Resolved scopes keyed by `${entity}.${action}`. A role may hold MULTIPLE scopes
 * for one entity.action (e.g. both `same_oe` and `child_oe`); the value is the SET
 * of granted scopes. Absent key or `[]` means no access. Enforcement is the UNION
 * (OR) of every listed scope's row filter.
 */
export type ScopeMap = Record<string, PermissionScope[]>;

/** An entity action normalized to a uniform shape (label is an i18n key, NOT translated). */
export interface NormalizedScopedAction {
  action: PermissionActionKey;
  label: string;
  scopes: readonly PermissionScope[];
}

/**
 * Map an entity's mixed `actions` array (bare CRUD strings and/or custom
 * descriptors) into uniform descriptors. Single source of truth for resolving an
 * action's i18n label and valid scopes — consumed by the matrix UI and by
 * `ScopedPermissionManager.getAllKeys()`.
 */
export function normalizeScopedActions(entity: IScopedEntity): NormalizedScopedAction[] {
  const entityScopes = entity.scopes ?? SCOPE_ORDER;
  return entity.actions.map((a) =>
    typeof a === 'string'
      ? { action: a, label: `permission.action.${a}`, scopes: entityScopes }
      : {
          action: a.action,
          label: a.label ?? `permission.action.${a.action}`,
          scopes: a.scopes ?? entityScopes,
        },
  );
}
